// The HTML of an announcement (pasted by the root admin at /root-admin/announcements) on its way into
// the popup's shadow root. The paste is parsed in an inert document, scrubbed there and the NODES are
// imported - never serialized and parsed again, so nothing can change shape on a second parse.
//
// What stays: markup, <style> (it applies to the shadow root alone), https stylesheets, inline SVG,
// pictures, <video>, and YouTube / Vimeo / Loom embeds. What goes: scripts and every other way to
// run code - on* attributes, javascript: / vbscript: urls, data: urls outside pictures, forms,
// objects, frames of any other site, <base> / <meta>, SVG animations that rewrite a link.

const DROP_TAGS = ['SCRIPT', 'NOSCRIPT', 'OBJECT', 'EMBED', 'APPLET', 'BASE', 'META', 'FRAME', 'FRAMESET', 'FORM', 'TEMPLATE', 'PORTAL', 'TITLE'];

const ANIMATION_TAGS = ['SET', 'ANIMATE', 'ANIMATETRANSFORM', 'ANIMATEMOTION'];

const VIDEO_EMBEDS = [
    /^https:\/\/(www\.)?youtube(-nocookie)?\.com\/embed\//i,
    /^https:\/\/player\.vimeo\.com\/video\//i,
    /^https:\/\/(www\.)?loom\.com\/embed\//i,
];

const URL_ATTRIBUTES = ['href', 'src', 'action', 'formaction', 'poster', 'background', 'srcset', 'data', 'ping', 'lowsrc', 'dynsrc'];

const PICTURE_DATA_URL = /^data:image\/(png|jpe?g|gif|webp|avif|svg\+xml)[;,]/;


function isUnsafeUrl(value, pictureAllowed) {

    // browsers ignore whitespace and control characters inside a scheme ("java\tscript:")
    let url = String(value || '').replace(/[\u0000- \u007f- ]/g, '').toLowerCase();

    if (url.indexOf('javascript:') > -1 || url.indexOf('vbscript:') > -1)
        return true;

    if (url.startsWith('data:'))
        return !(pictureAllowed && PICTURE_DATA_URL.test(url));

    return false;

}


function isStylesheet(element) {
    return /(^|\s)stylesheet(\s|$)/i.test(element.getAttribute('rel') || '') && /^https:\/\//i.test(element.getAttribute('href') || '');
}


function scrubAttributes(element, tag) {

    Array.from(element.attributes).forEach(attribute => {

        let name = attribute.name.toLowerCase();

        if (name.startsWith('on') || name === 'srcdoc' || name === 'formaction') {
            element.removeAttribute(attribute.name);
            return;
        }

        if (URL_ATTRIBUTES.indexOf(name) > -1 || name.endsWith(':href')) {

            let pictureAllowed = (name === 'src' || name === 'srcset' || name === 'poster') && tag !== 'IFRAME';

            if (isUnsafeUrl(attribute.value, pictureAllowed))
                element.removeAttribute(attribute.name);

        }

    });

}


function scrub(root) {

    Array.from(root.querySelectorAll('*')).forEach(element => {

        // an ancestor already went
        if (!root.contains(element))
            return;

        let tag = element.tagName.toUpperCase();

        if (DROP_TAGS.indexOf(tag) > -1) {
            element.remove();
            return;
        }

        if (tag === 'IFRAME' && !VIDEO_EMBEDS.some(pattern => pattern.test(element.getAttribute('src') || ''))) {
            element.remove();
            return;
        }

        if (tag === 'LINK' && !isStylesheet(element)) {
            element.remove();
            return;
        }

        if (ANIMATION_TAGS.indexOf(tag) > -1) {
            let target = (element.getAttribute('attributeName') || '').toLowerCase();
            if (target.indexOf('href') > -1 || target.startsWith('on')) {
                element.remove();
                return;
            }
        }

        scrubAttributes(element, tag);

        // links out of the app (https://… or //…) open in a new tab; app paths are followed by the
        // popup itself
        if (tag === 'A' && /^(https?:)?\/\//i.test(element.getAttribute('href') || '')) {
            element.setAttribute('target', '_blank');
            element.setAttribute('rel', 'noopener noreferrer');
        }

    });

}


// the paste → a fragment of this document, ready to append (a full page works too: its <head>
// styles are kept, the rest of the head is not)
export function buildAnnouncementFragment(html) {

    let parsed = new DOMParser().parseFromString(String(html || ''), 'text/html');

    let container = parsed.createElement('div');

    Array.from(parsed.head.children).forEach(node => {
        if (node.tagName === 'STYLE' || (node.tagName === 'LINK' && isStylesheet(node)))
            container.appendChild(node);
    });

    while (parsed.body.firstChild)
        container.appendChild(parsed.body.firstChild);

    scrub(container);

    let fragment = document.createDocumentFragment();

    Array.from(container.childNodes).forEach(node => fragment.appendChild(document.importNode(node, true)));

    return fragment;

}
